import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO_BLACK = '/images/logo-black.png';

const navItems = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/contact', label: 'Contact' },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? 'bg-ivory/95 backdrop-blur-xl' : 'bg-transparent'
            }`}
        >
            <div
                className={`absolute bottom-0 left-0 right-0 h-px bg-ink/10 transition-opacity duration-500 ${
                    scrolled ? 'opacity-100' : 'opacity-0'
                }`}
            />
            <div className="px-6 md:px-10 lg:px-16">
                <div className="flex items-center justify-between h-20 md:h-24">
                    <Link
                        to="/"
                        className="flex items-center gap-3 group"
                        data-testid="nav-logo"
                        aria-label="Nakasendo Capital — Home"
                    >
                        <img
                            src={LOGO_BLACK}
                            alt="Nakasendo Capital"
                            className="h-12 md:h-14 w-auto transition-opacity duration-500 group-hover:opacity-80"
                            loading="eager"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === '/'}
                                data-testid={`nav-link-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `relative font-sans text-[12px] font-medium uppercase tracking-[0.22em] transition-colors duration-300 ${
                                        isActive ? 'text-ink' : 'text-ink-soft hover:text-ink'
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <span className="relative inline-block py-2">
                                        {item.label}
                                        <span
                                            className={`absolute left-0 -bottom-0 h-px w-full bg-ink origin-left transition-transform duration-500 ${
                                                isActive ? 'scale-x-100' : 'scale-x-0'
                                            }`}
                                        />
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <Link
                        to="/contact"
                        data-testid="nav-cta-contact"
                        className="hidden md:inline-flex items-center gap-2 font-sans text-[12px] font-medium uppercase tracking-[0.22em] text-ink hover:text-aizome transition-colors duration-300"
                    >
                        <span className="link-underline">Begin Journey</span>
                        <span aria-hidden="true">→</span>
                    </Link>

                    {/* Mobile menu trigger */}
                    <button
                        data-testid="mobile-menu-toggle"
                        className="md:hidden p-2 text-ink"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Toggle menu"
                        aria-expanded={open}
                    >
                        {open ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            <div
                className={`md:hidden overflow-hidden bg-ivory transition-all duration-500 ease-out ${
                    open ? 'max-h-screen border-t border-ink/10' : 'max-h-0'
                }`}
                data-testid="mobile-menu"
            >
                <nav className="px-6 py-6 flex flex-col" aria-label="Mobile">
                    {navItems.map((item, idx) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === '/'}
                            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `font-serif text-3xl py-4 ${idx !== 0 ? 'border-t border-ink/10' : ''} ${
                                    isActive ? 'text-ink' : 'text-ink-soft'
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="mobile-nav-cta"
                        className="mt-6 btn-primary justify-center"
                    >
                        Begin Journey <span className="arrow">→</span>
                    </Link>
                </nav>
            </div>
        </header>
    );
};

export default Navbar;
